import React, { useState } from 'react';
import { api } from './api.js';
import { Card, Button, Field, inputCls } from './ui.jsx';

export default function SetupTab({ boot, onSaved }) {
  const ref = boot.reference;
  const [profile, setProfile] = useState(boot.settings.profile);
  const [contracts, setContracts] = useState((boot.settings.profile.contracts || []).join(', '));
  const [note, setNote] = useState(boot.settings.profile_note || '');
  const [reminders, setReminders] = useState((boot.settings.reminder_days || [21, 14, 7, 5, 3, 1]).join(', '));
  const [overdue, setOverdue] = useState(boot.settings.overdue_every_days || 7);
  const [escalate, setEscalate] = useState(boot.settings.escalate_after_days || 7);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = (list, key) => setProfile((p) => ({ ...p, [list]: p[list].includes(key) ? p[list].filter((k) => k !== key) : [...p[list], key] }));
  const save = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/settings', { method: 'PUT', body: { profile: { ...profile, contracts: contracts.split(/[,\s;]+/).filter(Boolean) }, profile_note: note, reminder_days: reminders.split(/[ ,]+/).map(Number).filter(Boolean), overdue_every_days: Number(overdue), escalate_after_days: Number(escalate) } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not save'); return; }
    setMsg('Saved.'); onSaved?.();
  };
  const reapply = async () => {
    setBusy(true); setMsg(null);
    const d = await api(`/cycles/${boot.cycle.id}/reapply`, { method: 'POST' });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not re-apply'); return; }
    setMsg(`Re-applied: ${d.owners_updated} owner lines and ${d.applicability_checked} applicability checks. Items you set by hand were left alone.`); onSaved?.();
  };

  return (
    <div className="space-y-6">
      <Card title="What the plan is" right={<span className="text-xs text-slate-500">decides which of the {boot.edition.item_count} CMS items apply</span>}>
        {boot.settings.profile_note && (
          <div className="mb-5 text-sm text-slate-700 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 leading-relaxed">{boot.settings.profile_note}</div>
        )}
        <div className="mb-5 max-w-md">
          <Field label="Medicare contracts" hint="H numbers, comma separated"><input className={inputCls} value={contracts} onChange={(e) => setContracts(e.target.value)} placeholder="H0982" /></Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Plan types offered</div>
            <div className="space-y-1.5">{ref.plan_types.map(([k, label]) => <Check key={k} checked={profile.plan_types.includes(k)} onChange={() => toggle('plan_types', k)}>{label}</Check>)}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Special needs plans</div>
            <div className="space-y-1.5">{ref.snp_types.map(([k, label]) => <Check key={k} checked={profile.snp_types.includes(k)} onChange={() => toggle('snp_types', k)}>{label}</Check>)}</div>
          </div>
        </div>
        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">A few yes or no facts</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">{ref.flags.map(([k, label]) => <Check key={k} checked={profile[k] === true} onChange={() => setProfile((p) => ({ ...p, [k]: !p[k] }))}>{label}</Check>)}</div>
        </div>
      </Card>

      <Card title="Where this profile came from" right={<span className="text-xs text-slate-500">kept with the record</span>}>
        <Field label="Note"><textarea rows={3} className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Source of the profile, what was confirmed and by whom" /></Field>
      </Card>

      <Card title="The clock" right={<span className="text-xs text-slate-500">one email per person, only what is still open</span>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Remind owners (days before due)" hint="21, 14, 7, 5, 3, 1 is three weeks, two weeks, one week, five, three and one day. A finished subsection is never mentioned; a person with nothing open gets nothing."><input className={inputCls} value={reminders} onChange={(e) => setReminders(e.target.value)} /></Field>
          <Field label="When overdue, remind every (days)"><input type="number" min="1" className={inputCls} value={overdue} onChange={(e) => setOverdue(e.target.value)} /></Field>
          <Field label="Escalate to the department lead after (days overdue)"><input type="number" min="1" className={inputCls} value={escalate} onChange={(e) => setEscalate(e.target.value)} /></Field>
        </div>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        {boot.cycle && <Button kind="secondary" onClick={reapply} disabled={busy}>Re-apply profile and roster to the open year</Button>}
        {msg && <span className="text-xs text-slate-600">{msg}</span>}
      </div>
    </div>
  );
}

function Check({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
      <input type="checkbox" className="rounded border-slate-300 text-orange-600 focus:ring-orange-200" checked={checked} onChange={onChange} />
      <span>{children}</span>
    </label>
  );
}
